import {MouseEventHandler} from "react";
import {css} from "@panda/css";

import {Badge} from "@pg-design/badge-module";
import {Button} from "@pg-design/button-module";
import {UnlockIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, pluralize} from "@pg-mono/string-utils";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {InquiryButtonTypeName} from "../../../constants/inquiry_button_type";
import {ApplicationModalButton} from "../../application/components/ApplicationModalButton";
import {ApplicationModalButtonWithHiddenPrices} from "../../application/components/ApplicationModalButtonWithHiddenPrices";
import {ApplicationFormPromotionPill} from "../../application/components/form_box/ApplicationFormPromotionPill";
import {ApplicationModalStep} from "../../application/types/ApplicationModalStep";
import {AdditionalFormFieldType} from "../../application/types/lead/AdditionalFormFieldType";
import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {LinkWithLabel} from "../../atoms/LinkWithLabel";
import {OfferDetailViewHeader} from "../../atoms/OfferDetailViewHeader";
import {IBoosterClickHandlerParams} from "../../boosters/types/IBoosterClickHandlerParams";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {useShouldShowPriceByOfferConfigAndUser} from "../../hooks/use_should_show_price_by_offer_config_and_user";
import {useOfferLink} from "../../offer/detail/hooks/use_offer_link";
import {getOfferTypeNameCapital} from "../../offer/helpers/OfferType";
import {getOfferDisplayStatus} from "../../offer/utils/get_offer_display_status";
import {IPropertyDetailPromotion} from "../actions/fetch_property_detail_promotions";
import {IPropertyDetail} from "../types/IPropertyDetail";
import {PropertyDisplayType} from "../types/PropertyDisplayType";
import {getPropertyHeaderButtonVariant, PropertyHeaderButtonVariant} from "../utils/get_property_header_button_variant";
import {PropertyDetailHeaderHiddenPriceVariantPrice} from "./PropertyDetailHeaderHiddenPriceVariantPrice";

interface IProps {
    property: IPropertyDetail;
    promotions: IPropertyDetailPromotion[] | null;
    onBoosterClick: (params: IBoosterClickHandlerParams) => void;
    onLocationClick: () => void;
    onOfferLinkClick?: MouseEventHandler<HTMLAnchorElement>;
    variant: "visible" | "blur";
}

const roomPlural = pluralize(["pokój", "pokoje", "pokoi"]);

export const PropertyDetailHeaderHiddenPriceVariant = (props: IProps) => {
    const {property} = props;

    const isApartment = property.offer.is_holiday_location || property.offer.is_condohotel;
    const isPublished = property.configuration.display_type === PropertyDisplayType.PUBLISHED;

    const offerLink = useOfferLink(property.offer);
    const vendorLink = rpAppLink.vendor.detail.base({vendorSlug: property.offer.vendor.slug, vendorId: property.offer.vendor.id});

    const {shouldHidePrices} = useShouldHidePrices();
    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: property.offer.configuration.price_visibility,
        vendorPriceVisibility: property.offer.vendor.configuration.price_visibility,
        vendorId: property.offer.vendor.id
    });

    const gtmDataVisible = {
        clickText: InquiryButtonTypeName.NEGOTIATE,
        source: GTMEventApplicationSourceSection.HEADER_NEGOTIATE,
        vendorName: property.offer.vendor.name ?? property.offer.vendor.id.toString(),
        offerName: property.offer.name ?? property.offer.id.toString()
    };

    const gtmDataHidden = {
        clickText: "Odkryj cenę",
        source: GTMEventApplicationSourceSection.HEADER_LEAD,
        vendorName: property.offer.vendor.name ?? property.offer.vendor.id.toString(),
        offerName: property.offer.name ?? property.offer.id.toString(),
        gtmSourceSection: GTMEventApplicationSourceSection.HEADER_LEAD
    };

    const titleText = `${isApartment ? "Apartament" : getOfferTypeNameCapital(property.offer.type)} na sprzedaż ${property.rooms} ${roomPlural(
        property.rooms
    )} o powierzchni ${areaFormat(property.area, {precision: 2})} numer ${property.number}`;

    const buttonVariant = getPropertyHeaderButtonVariant({
        propertyPrice: property.price,
        shouldShowPrice,
        shouldHidePrices,
        propsVariant: props.variant === "visible" ? PropertyHeaderButtonVariant.VISIBLE : PropertyHeaderButtonVariant.BLUR
    });
    const variant = shouldShowPrice ? "visible" : props.variant;

    const gtmDataFullOffer = {
        clickText: InquiryButtonTypeName.ASK_FOR_FULL_OFFER,
        source: GTMEventApplicationSourceSection.HEADER_LEAD,
        vendorName: property.offer.vendor.name ?? property.offer.vendor.id.toString(),
        offerName: property.offer.name ?? property.offer.id.toString()
    };

    const showReservationBadge = property.is_reserved;
    const showArchivedBadge = !property.for_sale || !isPublished;
    const showBadges = showReservationBadge || showArchivedBadge;

    return (
        <div className={wrapperStyle} data-testid={OFFER_TEST_IDS.PROPERTY_VIEW.PROPERTY_HEADER_SECTION}>
            <OfferDetailViewHeader
                mainContentClassName={mainContentStyle}
                mainHeadlineClassName={mainHeadlineTextStyle}
                address={property.offer.address}
                badges={
                    showBadges && (
                        <>
                            {showReservationBadge && (
                                <Badge variant="label_warning" icon={UnlockIcon}>
                                    Rezerwacja
                                </Badge>
                            )}

                            {showArchivedBadge && <Badge variant="label_secondary">Oferta archiwalna</Badge>}
                        </>
                    )
                }
                cta={
                    <div className={ctaWrapperStyle}>
                        <div className={ctaContainerStyle}>
                            <div className={priceContainerStyle}>
                                <PropertyDetailHeaderHiddenPriceVariantPrice property={property} variant={variant} />
                            </div>
                            {buttonVariant === PropertyHeaderButtonVariant.NO_PRICE ? (
                                <ApplicationModalButton
                                    source={ApplicationSource.Property}
                                    sourceSection={ApplicationSourceSection.MODAL}
                                    vendorId={property.offer.vendor.id}
                                    offerId={property.offer.id}
                                    propertyId={property.id}
                                    currency={property.offer.currency}
                                    forceClose={getOfferDisplayStatus(property.offer) === "limited"}
                                    render={({openModal}) => (
                                        <Button
                                            className={buttonStyle}
                                            size="medium"
                                            variant="outlined_secondary"
                                            onClick={() => openModal(gtmDataFullOffer)}
                                            dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OPEN_LEAD_FORM_HEADING}
                                        >
                                            Zapytaj o pełną ofertę
                                        </Button>
                                    )}
                                />
                            ) : buttonVariant === PropertyHeaderButtonVariant.BLUR ? (
                                <ApplicationModalButtonWithHiddenPrices
                                    source={shouldShowPrice ? ApplicationSource.Property : ApplicationSource.PriceInquiry}
                                    sourceSection={ApplicationSourceSection.MODAL}
                                    shouldShowPrice={shouldShowPrice}
                                    gtmData={gtmDataHidden}
                                    vendorId={property.offer.vendor.id}
                                    offerId={property.offer.id}
                                    propertyId={property.id}
                                    currency={property.offer.currency}
                                    forceClose={getOfferDisplayStatus(property.offer) === "limited"}
                                    render={({handleClick}) => (
                                        <Button
                                            className={buttonStyle}
                                            size="medium"
                                            variant="outlined_secondary"
                                            onClick={handleClick}
                                            dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OPEN_REGISTER_FORM_BUTTON}
                                        >
                                            Odkryj cenę
                                        </Button>
                                    )}
                                />
                            ) : (
                                <ApplicationModalButton
                                    source={ApplicationSource.NegotiatePrice}
                                    sourceSection={ApplicationSourceSection.MODAL}
                                    useAdditionalFormField={AdditionalFormFieldType.NEGOTIATION}
                                    initialStep={ApplicationModalStep.APPLICATION_FORM_WITH_NEGOTIATION}
                                    vendorId={property.offer.vendor.id}
                                    offerId={property.offer.id}
                                    propertyId={property.id}
                                    currency={property.offer.currency}
                                    forceClose={getOfferDisplayStatus(property.offer) === "limited"}
                                    render={({openModal}) => (
                                        <Button
                                            className={buttonStyle}
                                            size="medium"
                                            variant="outlined_secondary"
                                            onClick={() => openModal(gtmDataVisible)}
                                            dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OPEN_LEAD_FORM_HEADING}
                                        >
                                            Negocjuj cenę
                                        </Button>
                                    )}
                                />
                            )}
                        </div>

                        <ApplicationFormPromotionPill />
                    </div>
                }
                ctaClassName={ctaSectionStyle}
                externalLinks={
                    <h2 className={css({m: 0})}>
                        <LinkWithLabel
                            href={offerLink}
                            label="Inwestycja:"
                            linkText={property.offer.name}
                            dataTestId={OFFER_TEST_IDS.PROPERTY_VIEW.PROPERTY_OFFER_NAME}
                            onMouseDown={props.onOfferLinkClick}
                        />
                        {property.offer.investor ? (
                            <div className={investorWrapperStyle}>
                                <Text variant="info_txt_1" className={investorLabelStyle}>
                                    Inwestor:
                                </Text>
                                <Text variant="info_txt_1" className={investorNameStyle}>
                                    {property.offer.investor}
                                </Text>
                            </div>
                        ) : (
                            <LinkWithLabel
                                href={vendorLink}
                                label="Deweloper:"
                                linkText={property.offer.vendor.name}
                                dataTestId={OFFER_TEST_IDS.PROPERTY_VIEW.PROPERTY_VENDOR_NAME}
                            />
                        )}
                    </h2>
                }
                onLocationClick={props.onLocationClick}
                title={titleText}
            />
        </div>
    );
};

const wrapperStyle = css({
    display: "flex",
    position: "relative",
    flexWrap: "wrap",
    marginBottom: 2,

    md: {
        marginBottom: 0
    }
});

const mainContentStyle = css({
    flexDirection: "column",
    md: {
        width: "100%",
        flexWrap: "nowrap",
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "space-between"
    }
});

const mainHeadlineTextStyle = css({
    md: {
        alignSelf: "center"
    }
});

const investorWrapperStyle = css({
    lineHeight: "1.2rem",
    minHeight: "1.6rem"
});

const investorLabelStyle = css({
    marginRight: "0.2rem",
    color: "var(--colors-gray-700)",
    display: "inline"
});

const investorNameStyle = css({
    lineHeight: 0,
    display: "inline"
});

const ctaSectionStyle = css({
    display: "flex",
    gap: 2,
    flexDirection: "column",
    width: "100%",
    flex: "0 0 auto",
    md: {
        width: "auto",
        flexDirection: "row",
        alignItems: "center",
        gap: 3
    }
});

const ctaWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start"
});

const ctaContainerStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    mb: 2,
    gap: 2,
    md: {
        flexDirection: "row",
        gap: 3
    }
});

const priceContainerStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end"
});

const buttonStyle = css({
    px: 4
});
import {MouseEventHandler} from "react";
import {css} from "@panda/css";

import {Badge} from "@pg-design/badge-module";
import {Button} from "@pg-design/button-module";
import {UnlockIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {rpAppLink} from "@pg-mono/rp-routes";
import {areaFormat, pluralize} from "@pg-mono/string-utils";
import {OFFER_TEST_IDS} from "@pg-mono/test-ids";

import {InquiryButtonTypeName} from "../../../constants/inquiry_button_type";
import {ApplicationModalButton} from "../../application/components/ApplicationModalButton";
import {ApplicationModalButtonWithHiddenPrices} from "../../application/components/ApplicationModalButtonWithHiddenPrices";
import {ApplicationFormPromotionPill} from "../../application/components/form_box/ApplicationFormPromotionPill";
import {ApplicationModalStep} from "../../application/types/ApplicationModalStep";
import {AdditionalFormFieldType} from "../../application/types/lead/AdditionalFormFieldType";
import {GTMEventApplicationSourceSection} from "../../application/types/lead/GTMEvenApplicationSourceSection";
import {ApplicationSource, ApplicationSourceSection} from "../../application/utils/form/application_form_source";
import {LinkWithLabel} from "../../atoms/LinkWithLabel";
import {OfferDetailViewHeader} from "../../atoms/OfferDetailViewHeader";
import {IBoosterClickHandlerParams} from "../../boosters/types/IBoosterClickHandlerParams";
import {useShouldHidePrices} from "../../hooks/use_should_hide_prices";
import {useShouldShowPriceByOfferConfigAndUser} from "../../hooks/use_should_show_price_by_offer_config_and_user";
import {useOfferLink} from "../../offer/detail/hooks/use_offer_link";
import {getOfferTypeNameCapital} from "../../offer/helpers/OfferType";
import {getOfferDisplayStatus} from "../../offer/utils/get_offer_display_status";
import {IPropertyDetailPromotion} from "../actions/fetch_property_detail_promotions";
import {IPropertyDetail} from "../types/IPropertyDetail";
import {PropertyDisplayType} from "../types/PropertyDisplayType";
import {getPropertyHeaderButtonVariant, PropertyHeaderButtonVariant} from "../utils/get_property_header_button_variant";
import {PropertyDetailHeaderHiddenPriceVariantPrice} from "./PropertyDetailHeaderHiddenPriceVariantPrice";

interface IProps {
    property: IPropertyDetail;
    promotions: IPropertyDetailPromotion[] | null;
    onBoosterClick: (params: IBoosterClickHandlerParams) => void;
    onLocationClick: () => void;
    onOfferLinkClick?: MouseEventHandler<HTMLAnchorElement>;
    variant: "visible" | "blur";
}

const roomPlural = pluralize(["pokój", "pokoje", "pokoi"]);

export const PropertyDetailHeaderHiddenPriceVariant = (props: IProps) => {
    const {property} = props;

    const isApartment = property.offer.is_holiday_location || property.offer.is_condohotel;
    const isPublished = property.configuration.display_type === PropertyDisplayType.PUBLISHED;

    const offerLink = useOfferLink(property.offer);
    const vendorLink = rpAppLink.vendor.detail.base({vendorSlug: property.offer.vendor.slug, vendorId: property.offer.vendor.id});

    const {shouldHidePrices} = useShouldHidePrices();
    const shouldShowPrice = useShouldShowPriceByOfferConfigAndUser({
        offerPriceVisibility: property.offer.configuration.price_visibility,
        vendorPriceVisibility: property.offer.vendor.configuration.price_visibility,
        vendorId: property.offer.vendor.id
    });

    const gtmDataVisible = {
        clickText: InquiryButtonTypeName.NEGOTIATE,
        source: GTMEventApplicationSourceSection.HEADER_NEGOTIATE,
        vendorName: property.offer.vendor.name ?? property.offer.vendor.id.toString(),
        offerName: property.offer.name ?? property.offer.id.toString()
    };

    const gtmDataHidden = {
        clickText: "Odkryj cenę",
        source: GTMEventApplicationSourceSection.HEADER_LEAD,
        vendorName: property.offer.vendor.name ?? property.offer.vendor.id.toString(),
        offerName: property.offer.name ?? property.offer.id.toString(),
        gtmSourceSection: GTMEventApplicationSourceSection.HEADER_LEAD
    };

    const titleText = `${isApartment ? "Apartament" : getOfferTypeNameCapital(property.offer.type)} na sprzedaż ${property.rooms} ${roomPlural(
        property.rooms
    )} o powierzchni ${areaFormat(property.area, {precision: 2})} numer ${property.number}`;

    const buttonVariant = getPropertyHeaderButtonVariant({
        propertyPrice: property.price,
        shouldShowPrice,
        shouldHidePrices,
        propsVariant: props.variant === "visible" ? PropertyHeaderButtonVariant.VISIBLE : PropertyHeaderButtonVariant.BLUR
    });
    const variant = shouldShowPrice ? "visible" : props.variant;

    const gtmDataFullOffer = {
        clickText: InquiryButtonTypeName.ASK_FOR_FULL_OFFER,
        source: GTMEventApplicationSourceSection.HEADER_LEAD,
        vendorName: property.offer.vendor.name ?? property.offer.vendor.id.toString(),
        offerName: property.offer.name ?? property.offer.id.toString()
    };

    const showReservationBadge = property.is_reserved;
    const showArchivedBadge = !property.for_sale || !isPublished;
    const showBadges = showReservationBadge || showArchivedBadge;

    return (
        <div className={wrapperStyle} data-testid={OFFER_TEST_IDS.PROPERTY_VIEW.PROPERTY_HEADER_SECTION}>
            <OfferDetailViewHeader
                mainContentClassName={mainContentStyle}
                mainHeadlineClassName={mainHeadlineTextStyle}
                address={property.offer.address}
                badges={
                    showBadges && (
                        <>
                            {showReservationBadge && (
                                <Badge variant="label_warning" icon={UnlockIcon}>
                                    Rezerwacja
                                </Badge>
                            )}

                            {showArchivedBadge && <Badge variant="label_secondary">Oferta archiwalna</Badge>}
                        </>
                    )
                }
                cta={
                    <div className={ctaWrapperStyle}>
                        <div className={ctaContainerStyle}>
                            <div className={priceContainerStyle}>
                                <PropertyDetailHeaderHiddenPriceVariantPrice property={property} variant={variant} />
                            </div>
                            {buttonVariant === PropertyHeaderButtonVariant.NO_PRICE ? (
                                <ApplicationModalButton
                                    source={ApplicationSource.Property}
                                    sourceSection={ApplicationSourceSection.MODAL}
                                    vendorId={property.offer.vendor.id}
                                    offerId={property.offer.id}
                                    propertyId={property.id}
                                    currency={property.offer.currency}
                                    forceClose={getOfferDisplayStatus(property.offer) === "limited"}
                                    render={({openModal}) => (
                                        <Button
                                            className={buttonStyle}
                                            size="medium"
                                            variant="outlined_secondary"
                                            onClick={() => openModal(gtmDataFullOffer)}
                                            dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OPEN_LEAD_FORM_HEADING}
                                        >
                                            Zapytaj o pełną ofertę
                                        </Button>
                                    )}
                                />
                            ) : buttonVariant === PropertyHeaderButtonVariant.BLUR ? (
                                <ApplicationModalButtonWithHiddenPrices
                                    source={shouldShowPrice ? ApplicationSource.Property : ApplicationSource.PriceInquiry}
                                    sourceSection={ApplicationSourceSection.MODAL}
                                    shouldShowPrice={shouldShowPrice}
                                    gtmData={gtmDataHidden}
                                    vendorId={property.offer.vendor.id}
                                    offerId={property.offer.id}
                                    propertyId={property.id}
                                    currency={property.offer.currency}
                                    forceClose={getOfferDisplayStatus(property.offer) === "limited"}
                                    render={({handleClick}) => (
                                        <Button
                                            className={buttonStyle}
                                            size="medium"
                                            variant="outlined_secondary"
                                            onClick={handleClick}
                                            dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OPEN_REGISTER_FORM_BUTTON}
                                        >
                                            Odkryj cenę
                                        </Button>
                                    )}
                                />
                            ) : (
                                <ApplicationModalButton
                                    source={ApplicationSource.NegotiatePrice}
                                    sourceSection={ApplicationSourceSection.MODAL}
                                    useAdditionalFormField={AdditionalFormFieldType.NEGOTIATION}
                                    initialStep={ApplicationModalStep.APPLICATION_FORM_WITH_NEGOTIATION}
                                    vendorId={property.offer.vendor.id}
                                    offerId={property.offer.id}
                                    propertyId={property.id}
                                    currency={property.offer.currency}
                                    forceClose={getOfferDisplayStatus(property.offer) === "limited"}
                                    render={({openModal}) => (
                                        <Button
                                            className={buttonStyle}
                                            size="medium"
                                            variant="outlined_secondary"
                                            onClick={() => openModal(gtmDataVisible)}
                                            dataTestId={OFFER_TEST_IDS.OFFER_VIEW.OPEN_LEAD_FORM_HEADING}
                                        >
                                            Negocjuj cenę
                                        </Button>
                                    )}
                                />
                            )}
                        </div>

                        <ApplicationFormPromotionPill />
                    </div>
                }
                ctaClassName={ctaSectionStyle}
                externalLinks={
                    <h2 className={css({m: 0})}>
                        <LinkWithLabel
                            href={offerLink}
                            label="Inwestycja:"
                            linkText={property.offer.name}
                            dataTestId={OFFER_TEST_IDS.PROPERTY_VIEW.PROPERTY_OFFER_NAME}
                            onMouseDown={props.onOfferLinkClick}
                        />
                        {property.offer.investor ? (
                            <div className={investorWrapperStyle}>
                                <Text variant="info_txt_1" className={investorLabelStyle}>
                                    Inwestor:
                                </Text>
                                <Text variant="info_txt_1" className={investorNameStyle}>
                                    {property.offer.investor}
                                </Text>
                            </div>
                        ) : (
                            <LinkWithLabel
                                href={vendorLink}
                                label="Deweloper:"
                                linkText={property.offer.vendor.name}
                                dataTestId={OFFER_TEST_IDS.PROPERTY_VIEW.PROPERTY_VENDOR_NAME}
                            />
                        )}
                    </h2>
                }
                onLocationClick={props.onLocationClick}
                title={titleText}
            />
        </div>
    );
};

const wrapperStyle = css({
    display: "flex",
    position: "relative",
    flexWrap: "wrap",
    marginBottom: 2,

    md: {
        marginBottom: 0
    }
});

const mainContentStyle = css({
    flexDirection: "column",
    md: {
        width: "100%",
        flexWrap: "nowrap",
        flexDirection: "row",
        alignItems: "flex-start",
        justifyContent: "space-between"
    }
});

const mainHeadlineTextStyle = css({
    md: {
        alignSelf: "center"
    }
});

const investorWrapperStyle = css({
    lineHeight: "1.2rem",
    minHeight: "1.6rem"
});

const investorLabelStyle = css({
    marginRight: "0.2rem",
    color: "var(--colors-gray-700)",
    display: "inline"
});

const investorNameStyle = css({
    lineHeight: 0,
    display: "inline"
});

const ctaSectionStyle = css({
    display: "flex",
    gap: 2,
    flexDirection: "column",
    width: "100%",
    flex: "0 0 auto",
    md: {
        width: "auto",
        flexDirection: "row",
        alignItems: "center",
        gap: 3
    }
});

const ctaWrapperStyle = css({
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-start"
});

const ctaContainerStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    mb: 2,
    gap: 2,
    md: {
        flexDirection: "row",
        gap: 3
    }
});

const priceContainerStyle = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end"
});

const buttonStyle = css({
    px: 4
});
